import type { Casework } from "building-types/casework/caseworkTypes";
import type { ControllerPlacementCandidateTable } from "building-types/electrical/candidate";
import type { ElectricalController, ElectricalLoad } from "building-types/electrical/electricalDeviceTypes";
import { LoadConnectionType } from "building-types/electrical/electricalDeviceTypesEnum";
import type { InPlaceElectricalLayout } from "building-types/electrical/electricalInPlaceLayoutTypes";
import { ElectricalLayoutType } from "building-types/electrical/electricalLayoutTypesEnum";
import type {
  LegacyRoomLayoutController,
  LegacyRoomLayoutElectrical,
} from "building-types/electrical/electricalLegacyRoomLayoutTypes";
import type { ElectricalSlots } from "building-types/electrical/electricalSlots";
import { ControllerType, LoadType } from "building-types/electrical/electricalTypesEnum";
import type {
  ClearRoomTypeElectricalTable,
  Controller,
  GeneratedElectricalGroup,
  Load,
} from "building-types/electrical/generatedElectricalTypes";
import type { ReferenceGeom } from "building-types/entities/referenceGeom";
import type { ElectricalLayoutHandle } from "building-types/handles/docEntityHandleTypes";
import type { Prop, PropAttributeTable } from "building-types/props/propTypes";
import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { ElectricalItemCatalogSettings } from "building-types/settings/electricalItemCatalogSettings";
import type { WallMassSystem, WallSystem } from "building-types/walls/wallSytemTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsString } from "common/utils/assertOfType";
import objectFilter from "common/utils/objectFilter";
import uid from "common/utils/uid";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import type { MergedPropLibrary } from "doc/propTools";
import type { RoomIdToRoomSetIndexLookup, RoomSetWithLevelId } from "doc/rooms/roomSetTypes";
import { makeBuiltInLabelTable } from "doc/utils/labelTools";
import { aabb2ByPoints, aabb2Center } from "geom/aabb2";
import type { Vec2 } from "geom/vec2";
import { vec2Copy, vec2Dist } from "geom/vec2";
import { commitControllerSelection } from "../controllerSelection/controllerSelectionCommit";
import type { ControllerSelectionFunc } from "../controllerSelection/controllerSelectionShared";
import { convertControllerSelectionFuncToRoomSetIndexFunc } from "../controllerSelection/controllerSelectionShared";
import { controllerTypeFromSlots } from "../controllerTypeFromSlots";
import type { RoomSetTable, RuleToRoomFlagTable } from "../electricalLayoutTypes";
import type { RoomSetInfoTable } from "../electricalRoomSetInfo";
import { makeElectricalLayout } from "../roomLayouts/electricalLayouts";
import { embedHandleInElectricalGroups, type ElectricalGroupsByRoomOrDocResults } from "./electricalGroupsShared";
import { getLoadsAndPrimaryRoomsetFromRoomLayout } from "./electricalLoadPrePassForControllerSelectionFromRoomBlockDoc";
import { resolveLoadPosition } from "./resolveRoomPosition";

interface ElectricalGroupsFromRoomBlockDocInfo {
  readonly legacyRoomLayoutElectrical: Table<LegacyRoomLayoutElectrical>;
  readonly controllerSelectionFunc: ControllerSelectionFunc;
  readonly roomTable: Table<Room>;
  readonly slots: ElectricalSlots;
  readonly props: Table<Prop>;
  readonly casework: Table<Casework>;
  readonly referenceGeom: Table<ReferenceGeom>;
  readonly propCatalog: PropAttributeTable;
  readonly mergedPropLibrary: MergedPropLibrary;
  readonly electricalItemCatalogSettings: ElectricalItemCatalogSettings;
  readonly roomIdToRoomSetIndexLookup: RoomIdToRoomSetIndexLookup;
  readonly roomSetInfoTable: RoomSetInfoTable;
  readonly mirror: boolean;
  readonly roomOutlines: Table<RoomOutlines>;
  readonly wallSystems: Table<WallSystem>;
  readonly candidateTable: ControllerPlacementCandidateTable;
  readonly roomSets: RoomSetWithLevelId[];
  readonly wallMasses: Table<WallMassSystem>;
  readonly roomIndex: RoomIndex;
  readonly wallLayerExtentsCache: WallLayerExtentsCache;
  readonly electricalControllers: Table<ElectricalController>;
  readonly electricalLoads: Table<ElectricalLoad>;
  readonly entityGroups: Table<string>;
  readonly electricalLayouts: Table<InPlaceElectricalLayout>;
}

export async function electricalGroupsFromRoomBlockDoc({
  legacyRoomLayoutElectrical,
  controllerSelectionFunc,
  roomTable,
  props,
  casework,
  referenceGeom: docRefGeom,
  propCatalog,
  mergedPropLibrary,
  electricalItemCatalogSettings,
  slots,
  mirror,
  roomOutlines,
  wallSystems,
  candidateTable,
  roomIdToRoomSetIndexLookup,
  roomSets,
  wallMasses,
  roomIndex,
  wallLayerExtentsCache,
  electricalLoads,
  electricalControllers,
  entityGroups,
  electricalLayouts,
}: ElectricalGroupsFromRoomBlockDocInfo): Promise<ElectricalGroupsByRoomOrDocResults> {
  const clearRoomTypeElectrical: ClearRoomTypeElectricalTable = {};
  const electricalGroups: GeneratedElectricalGroup[] = [];

  // new simple electrical layouts
  for (const electricalLayoutid in electricalLayouts) {
    const electricalLayout = electricalLayouts[electricalLayoutid];
    const roomId = electricalLayout.parentId;
    const room = roomId ? roomTable[roomId] : undefined;
    if (!room) {
      continue;
    }

    assertIsString(roomId);
    const wallSystem = wallSystems[room.level.id];
    const wallIndex = wallSystem.wallIndex;
    const roomSetId = roomIdToRoomSetIndexLookup[roomId];
    const roomSet = roomSets[roomSetId];

    const ruleCanBeAppliedToRoomTable: RuleToRoomFlagTable = {};
    const roomSetTable: RoomSetTable = {};
    const roomsInRoomSetRuleCanBeAppliedTo = {};

    for (const { id: roomId } of roomSet.roomSet) {
      const roomSetRoom = roomTable[roomId];
      roomSetTable[roomId] = roomSetRoom;
      const roomFlagTable = ruleCanBeAppliedToRoomTable[roomId] || {};
      roomFlagTable[roomId] = true;
      ruleCanBeAppliedToRoomTable[roomId] = roomFlagTable;

      roomsInRoomSetRuleCanBeAppliedTo[roomId] = true;
    }

    const roomProps = Object.values(props).filter((x) => x?.room?.id === roomId);
    const roomOutlineTable = roomOutlines[roomId];
    const layoutElectricalGroups = await makeElectricalLayout(electricalLayout.layout, {
      roomSetTable,
      room,
      wallIndex,
      candidateTable,
      controllerSelectionFunc: convertControllerSelectionFuncToRoomSetIndexFunc(controllerSelectionFunc, roomId),
      roomProps,
      propCatalog,
      mergedPropLibrary,
      electricalItemCatalogSettings,
      elecSlots: slots,
      mirror,
      roomOutlines: roomOutlineTable,
      ruleAppliedToRoomTable: {},
      roomsInRoomSetRuleCanBeAppliedTo,
    });

    if (!layoutElectricalGroups) {
      continue;
    }
    const electricalLayoutHandle: ElectricalLayoutHandle = { type: "ELECTRICAL_LAYOUT", id: electricalLayoutid };
    embedHandleInElectricalGroups(layoutElectricalGroups, electricalLayoutHandle, electricalLayoutHandle);

    electricalGroups.push(...layoutElectricalGroups);
  }

  // new simple electrical groups
  const adHocGroups = makeTemporaryElectricalGroups(electricalLoads, electricalControllers, entityGroups);
  for (const groupId in adHocGroups) {
    const { electricalLoads, electricalControllers } = adHocGroups[groupId];

    const loadItems: Load[] = [];
    const contingentLoadsFromHomeLoadMigration: Load[] = [];
    let primaryRoomId: string | undefined = undefined;
    for (const load of electricalLoads) {
      const roomId = load.parentId;
      const room = roomTable[roomId];
      if (!room || !room.outline) {
        continue;
      }

      if (!primaryRoomId) {
        primaryRoomId = roomId;
      }

      const roomProps = objectFilter(props, (x) => x?.room?.id === roomId);
      const roomCasework = objectFilter(casework, (x) => x.room.id === roomId);
      const newLoadItems = await resolveLoadPosition(
        load,
        room,
        docRefGeom,
        roomProps,
        roomCasework,
        propCatalog,
        mergedPropLibrary,
        electricalItemCatalogSettings,
        mirror,
        roomOutlines,
        wallMasses,
        roomIndex,
        wallLayerExtentsCache,
      );
      if (load.fromLegacyHomeGroup) {
        contingentLoadsFromHomeLoadMigration.push(...newLoadItems);
        continue;
      }
      loadItems.push(...newLoadItems);
    }

    //some loads from deprecated home groups are dependent on other references to exist
    if (electricalControllers.length && contingentLoadsFromHomeLoadMigration.length) {
      loadItems.push(...contingentLoadsFromHomeLoadMigration);
    }

    const result = primaryRoomId ? roomTable[primaryRoomId] : undefined;
    if (result === undefined) {
      continue;
    }
    const {
      level: { id: levelId },
    } = result;

    if (loadItems.length === 0) {
      continue;
    }

    const virtualLoadItems: Load[] = [];
    for (const loadItem of loadItems || []) {
      if (loadItem.loadType === LoadType.CEILING_FAN_LIGHT) {
        virtualLoadItems.push({ ...loadItem, loadType: LoadType.VIRTUAL_CEILING_FAN_LIGHT });
      }
    }

    const approximateLoadPosition = loadItems.length
      ? aabb2Center(aabb2ByPoints(loadItems.map((x) => x.position)))
      : null;

    const controllers = await addControllers(
      electricalControllers,
      loadItems,
      approximateLoadPosition ? vec2Copy(approximateLoadPosition) : null,
      controllerSelectionFunc,
      roomTable,
    );

    const electricalGroup: GeneratedElectricalGroup = {
      handleForLinks: { type: "GROUP", id: groupId },
      levelId: levelId,
      loads: loadItems,
      controllers,
    };

    if (!electricalGroup) {
      continue;
    }

    electricalGroups.push(electricalGroup);

    for (const virtualLoadItem of virtualLoadItems) {
      assertIsString(primaryRoomId);
      const elecGroup = await makeElectricalGroupFromRoomLayoutInfo(
        [virtualLoadItem],
        primaryRoomId,
        slots,
        electricalControllers,
        controllerSelectionFunc,
        roomTable,
      );
      if (!elecGroup) {
        continue;
      }

      electricalGroups.push(elecGroup);
    }
  }

  // room layout
  for (const roomId in legacyRoomLayoutElectrical) {
    const roomGroups = legacyRoomLayoutElectrical[roomId];
    const groups = roomGroups.embeddedGroups;
    for (const { clearExistingLoadsInRoom, controllers, loads } of groups) {
      if (clearExistingLoadsInRoom) {
        addToRoomTypeElecClearTable([roomId], clearRoomTypeElectrical);
      }

      const result = await getLoadsAndPrimaryRoomsetFromRoomLayout(
        loads,
        slots,
        roomTable,
        roomId,
        props,
        propCatalog,
        mergedPropLibrary,
        electricalItemCatalogSettings,
        roomOutlines,
        mirror,
      );
      if (!result) {
        continue;
      }

      const { loads: loadItems, primaryRoomId } = result;

      const elecGroup = await makeElectricalGroupFromRoomLayoutInfo(
        loadItems,
        primaryRoomId,
        slots,
        controllers,
        controllerSelectionFunc,
        roomTable,
      );
      if (!elecGroup) {
        continue;
      }

      electricalGroups.push(elecGroup);
    }
  }

  return {
    electricalGroups,
    clearRoomTypeElectrical,
  };
}

function addToRoomTypeElecClearTable(
  clearExistingLoadRoomIds: string[],
  clearRoomTypeElec: ClearRoomTypeElectricalTable,
) {
  for (const clearId of clearExistingLoadRoomIds) {
    clearRoomTypeElec[clearId] = true;
  }
}

async function makeElectricalGroupFromRoomLayoutInfo(
  loadItems: Load[],
  primaryRoomId: string,
  slots: ElectricalSlots,
  controllerPlacementConnections: LegacyRoomLayoutController[],
  selectionFunc: ControllerSelectionFunc,
  roomTable: Table<Room>,
): Promise<GeneratedElectricalGroup> {
  const primaryRoom = roomTable[primaryRoomId];
  const {
    level: { id: levelId },
  } = primaryRoom;
  const approximateLoadPosition = aabb2Center(aabb2ByPoints(loadItems.map((x) => x.position)));

  const controllers = await addControllersFromRoomLayout(
    controllerPlacementConnections,
    slots,
    loadItems,
    approximateLoadPosition,
    selectionFunc,
    primaryRoom,
    levelId,
  );

  return {
    levelId: levelId,
    loads: loadItems,
    controllers,
  };
}

async function addControllersFromRoomLayout(
  controllerPlacementConnections: LegacyRoomLayoutController[],
  slots: ElectricalSlots,
  loadItems: Load[],
  approximateLoadPosition: Vec2,
  selectionFunc: ControllerSelectionFunc,
  primaryRoom: Room,
  levelId: string,
): Promise<Controller[]> {
  if (!controllerPlacementConnections) {
    return [];
  }

  const controllerItems: Controller[] = [];

  for (const {
    connection: connections,
    controllerType: rawControllerType,
    controllerTypeSlot,
    switchType,
    selections,
  } of controllerPlacementConnections) {
    const cType = rawControllerType ? rawControllerType : controllerTypeFromSlots(controllerTypeSlot, slots);
    const candidate = await selectionFunc(selections, primaryRoom.id, approximateLoadPosition);
    const controllerPosition = commitControllerSelection(
      candidate,
      ElectricalLayoutType.EXPLICIT,
      cType,
      approximateLoadPosition,
    );

    if (!controllerPosition) {
      // console.warn("controller position not set");
      continue;
    }

    let loadIndex: number;
    const [x, y] = controllerPosition.position;

    switch (connections.type) {
      case LoadConnectionType.EXPLICIT_LOAD_INDEX: {
        loadIndex = connections.loadIndex;
        break;
      }
      case LoadConnectionType.NEAREST_LOAD: {
        let closestIndex = 0;
        let minDist = 10000;
        for (const iString of Object.keys(loadItems)) {
          const i = parseInt(iString);
          const loadItem = loadItems[i];
          const [lX, lY] = loadItem.position;
          const dist = vec2Dist(lX, lY, x, y);
          if (dist < minDist) {
            closestIndex = i;
            minDist = dist;
          }
        }
        loadIndex = closestIndex;
        break;
      }
      case LoadConnectionType.NEAREST_LOAD_ARRAY_TERMINUS: {
        if (loadItems.length === 1) {
          loadIndex = 0;
        }

        const [sX, sY] = loadItems[0].position;
        const [eX, eY] = loadItems[loadItems.length - 1].position;
        const closestIndex = vec2Dist(sX, sY, x, y) > vec2Dist(eX, eY, x, y) ? loadItems.length - 1 : 0;
        loadIndex = closestIndex;

        break;
      }
    }

    controllerItems.push({
      type: cType,
      position: controllerPosition,
      switchType,
      loadIndex,
      candidateId: candidate?.candidate?.id,
      levelId,
      sourceRoomHandle: primaryRoom.roomHandle,
      labels: makeBuiltInLabelTable("addToElectricalPlan"),
    });
  }

  return controllerItems;
}

async function addControllers(
  electricalControllers: ElectricalController[],
  loadItems: Load[],
  approximateLoadPosition: Vec2 | null,
  selectionFunc: ControllerSelectionFunc,
  roomTable: Table<Room>,
): Promise<Controller[]> {
  if (!electricalControllers) {
    return [];
  }

  const controllerItems: Controller[] = [];

  for (const controller of electricalControllers) {
    const { connection, controllerType, parentId, selections } = controller;
    const candidate = await selectionFunc(selections, parentId, approximateLoadPosition ?? undefined);
    const controllerPosition = commitControllerSelection(
      candidate,
      ElectricalLayoutType.EXPLICIT,
      controllerType as ControllerType, // casting here to keep behavior and bypass strict null
      approximateLoadPosition ?? undefined,
    );

    if (!controllerPosition) {
      // console.warn("controller position not set");
      continue;
    }

    let loadIndex: number;
    const [x, y] = controllerPosition.position;

    switch (connection.type) {
      case LoadConnectionType.EXPLICIT_LOAD_INDEX: {
        loadIndex = connection.loadIndex;
        break;
      }
      case LoadConnectionType.NEAREST_LOAD: {
        let closestIndex = 0;
        let minDist = 10000;
        for (const iString of Object.keys(loadItems)) {
          const i = parseInt(iString);
          const loadItem = loadItems[i];
          const [lX, lY] = loadItem.position;
          const dist = vec2Dist(lX, lY, x, y);
          if (dist < minDist) {
            closestIndex = i;
            minDist = dist;
          }
        }
        loadIndex = closestIndex;
        break;
      }
      case LoadConnectionType.NEAREST_LOAD_ARRAY_TERMINUS: {
        if (loadItems.length === 1) {
          loadIndex = 0;
        }

        const [sX, sY] = loadItems[0].position;
        const [eX, eY] = loadItems[loadItems.length - 1].position;
        const closestIndex = vec2Dist(sX, sY, x, y) > vec2Dist(eX, eY, x, y) ? loadItems.length - 1 : 0;
        loadIndex = closestIndex;

        break;
      }
    }

    controllerItems.push({
      type: controllerType as ControllerType, // casting here to keep behavior and bypass strict null
      position: controllerPosition,
      switchType: controller.controllerType === ControllerType.SWITCH ? controller.switchType : undefined,
      planTag: controller.planTag,
      labels: controller.labels,
      loadIndex,
      candidateId: candidate?.candidate?.id,
      levelId: roomTable[parentId].level.id,
      sourceRoomHandle: roomTable[parentId].roomHandle,
      handle: { type: "ELECTRICAL_DEVICE", id: controller.id },
    });
  }

  return controllerItems;
}

interface TemporaryElectricalGroup {
  readonly electricalLoads: ElectricalLoad[];
  readonly electricalControllers: ElectricalController[];
}

/**
 * Makes temporary electrical groups from electrical loads and controllers, these groups are internal
 * to this code file and are used to create generated electrical groups.
 */
function makeTemporaryElectricalGroups(
  electricalLoads: Table<ElectricalLoad>,
  electricalControllers: Table<ElectricalController>,
  entityGroups: Table<string>,
): Table<TemporaryElectricalGroup> {
  const groups: RWTable<TemporaryElectricalGroup> = {};

  for (const id in electricalLoads) {
    const groupId = entityGroups[docEntityHandleHash({ type: "ELECTRICAL_DEVICE", id })];
    if (!groupId) {
      const newGroupId = uid();
      const group = { electricalLoads: [] as ElectricalLoad[], electricalControllers: [] };
      group.electricalLoads.push(electricalLoads[id]);
      groups[newGroupId] = group;
      continue;
    }

    const group = groups[groupId] || { electricalLoads: [], electricalControllers: [] };
    group.electricalLoads.push(electricalLoads[id]);
    groups[groupId] = group;
  }

  for (const id in electricalControllers) {
    const groupId = entityGroups[docEntityHandleHash({ type: "ELECTRICAL_DEVICE", id })];
    if (!groupId) {
      continue;
    }

    const group = groups[groupId];
    //if a group doesn't exist with loads in it, we don't care about the controllers
    if (!group) {
      continue;
    }
    group.electricalControllers.push(electricalControllers[id]);
    groups[groupId] = group;
  }

  for (const groupId in groups) {
    const group = groups[groupId];
    // sort loads and controllers by sortIndex
    group.electricalLoads.sort((a, b) => a.sortIndex - b.sortIndex);
    group.electricalControllers.sort((a, b) => a.sortIndex - b.sortIndex);
  }

  return groups;
}
